//@flow
import _ from 'lodash';
import StringUtils from 'common/utils/StringUtils';

class NumberUtils {
    static padLeft(n?: ?string | number): ?string {
        const number = Number(n);
        if (StringUtils.isEmpty(number)) return null;
        return (number < 10 ? '0' : '') + number;
    }

    static shorten(
        number: number,
        appendCharacters: string,
        threshold: number
    ): string | number {
        if (number > threshold) {
            const shortNumber = NumberUtils.roundOff(number / threshold);
            return `${shortNumber}${appendCharacters}`;
        }
        return number;
    }

    static formatNumber(
        n?: ?string | number,
        roundOff: boolean = false,
        decimals: number = 0
    ): ?string {
        let number = Number(n);
        if (StringUtils.isEmpty(number)) return null;
        number = roundOff ? NumberUtils.round(number, decimals) : number;
        return NumberUtils.padLeft(number);
    }

    static round(value?: ?string | number, decimals: number): ?number {
        const number = Number(value);
        if (StringUtils.isEmpty(value)) return null;
        return _.round(number, decimals);
    }

    // there are at least 5 ways to convert string to number.
    // the methods below are chosen becuase of performance and most edge cases handled.
    // ref: https://coderwall.com/p/5tlhmw/converting-strings-to-number-in-javascript-pitfalls
    // parseFloat is chosen because it, along with isNaN and isFinite
    // handle the most edge cases when tested with typical
    // javascript datatypes and values.
    static toNumber(value: any): number {
        const number = parseFloat(value);
        if (!isNaN(number) && isFinite(value)) {
            return number;
        } else {
            throw new Error(
                `Connot convert ${value} to a number. use a valid number for conversion.`
            );
        }
    }

    static isNumeric(value: any): boolean {
        return !isNaN(parseFloat(value)) && isFinite(value);
    }

    static isInteger(value: any): boolean {
        return (
            NumberUtils.isNumeric(value) &&
            Number.isInteger(NumberUtils.toNumber(value))
        );
    }

    static isPositiveNumber(value: any): boolean {
        return NumberUtils.isNumeric(value) && NumberUtils.toNumber(value) > 0;
    }

    static isNonNegativeNumber(value: any): boolean {
        return NumberUtils.isNumeric(value) && NumberUtils.toNumber(value) >= 0;
    }

    static isWholeNumber(value: any): boolean {
        return NumberUtils.isInteger(value) && NumberUtils.toNumber(value) >= 0;
    }

    static guid(): string {
        const s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };
        return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    }

    static getNumberSubtractedByPercentage(
        numberToSubstract: number,
        percentageValue: number
    ): number {
        return numberToSubstract - numberToSubstract * (percentageValue / 100);
    }

    static roundOff(n: string | number): ?number {
        return NumberUtils.round(n, 2);
    }

    static findNonZeroDecimalDigitIndex(number: number): number {
        const numberString = number.toString();
        const [, decimalDigits] = numberString.split('.');
        const decimalDigitsArray = decimalDigits.split('');

        const nonZeroFirstDecimalDigitIndex = decimalDigitsArray.findIndex(
            value => value !== '0'
        );

        if (nonZeroFirstDecimalDigitIndex >= 0) {
            return nonZeroFirstDecimalDigitIndex;
        } else {
            return 0;
        }
    }

    static roundOffWithNonZeroDecimal(
        number: ?number,
        decimalDigits: number = 2
    ): number | null {
        if (number === undefined || number === null) {
            return null;
        }

        // return number as it is when decimal digits is 0
        if (number.toString().indexOf('.') === -1) {
            return number;
        }

        const nonZeroDecimalDigitIndex =
            this.findNonZeroDecimalDigitIndex(number) + 1;

        return _.round(
            number,
            decimalDigits < nonZeroDecimalDigitIndex
                ? nonZeroDecimalDigitIndex
                : decimalDigits
        );
    }

    static romanize(number: number): string {
        if (isNaN(number)) {
            throw new Error(
                `Cannot convert ${number} to a Roman number. Use a valid number for conversion.`
            );
        }
        const digits = String(+number).split('');
        const key = [
            '',
            'C',
            'CC',
            'CCC',
            'CD',
            'D',
            'DC',
            'DCC',
            'DCCC',
            'CM',
            '',
            'X',
            'XX',
            'XXX',
            'XL',
            'L',
            'LX',
            'LXX',
            'LXXX',
            'XC',
            '',
            'I',
            'II',
            'III',
            'IV',
            'V',
            'VI',
            'VII',
            'VIII',
            'IX'
        ];
        let roman = '';
        let i = 3;
        while (i--) roman = (key[+digits.pop() + i * 10] || '') + roman;
        return Array(+digits.join('') + 1).join('M') + roman;
    }
}

export default NumberUtils;
